import { Link } from 'react-router-dom';
import type { CSSProperties, ReactNode } from 'react';
import { Icon, type IconName } from '@/components/Icon';

// docs/work_log/v1204-ui-polish — Shared dashboard primitives.
// 3 team dashboard 가 동일 visual language (skeleton / KPI card link / horizontal bar) 사용.

export const SHARED_CARD: CSSProperties = {
  background: 'var(--bg-surface)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  padding: 16,
  boxShadow: 'var(--shadow-sm)',
};

export const SHARED_CARD_TITLE: CSSProperties = {
  fontSize: 13,
  color: 'var(--fg-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 8,
};

/** 칸(Section) 제목 — 2026-10-27 오너 "파트별 제목들 크기좀 키워주고". 숫자 카드 제목은 SHARED_CARD_TITLE 그대로. */
export const SECTION_TITLE: CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: 'var(--fg-default)',
  marginBottom: 8,
};

export const SHARED_BIG: CSSProperties = { fontSize: 28, fontWeight: 700 };

export const SHARED_EMPTY_HINT: CSSProperties = {
  padding: '8px 0',
  fontSize: 12,
  color: 'var(--fg-muted)',
  fontStyle: 'italic',
};

// 5 분
export const SHARED_REFETCH_INTERVAL_MS = 5 * 60_000;

const SKELETON_BOX_BASE: CSSProperties = {
  background:
    'linear-gradient(90deg, var(--shimmer-base) 0%, var(--shimmer-highlight) 50%, var(--shimmer-base) 100%)',
  backgroundSize: '200% 100%',
  borderRadius: 4,
  animation: 'bh-shimmer 1.5s infinite',
};

/** Skeleton 의 keyframes inline 정의. 한 페이지 안에서 1번만 mount.
 *  Direction matches `tableSkeletonShimmer` in globals.css (left → right)
 *  so two skeletons on the same page animate in unison. */
export function ShimmerKeyframes() {
  return (
    <style>{`
      @keyframes bh-shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
    `}</style>
  );
}

/**
 * KPI 카드 (clickable). hover 시 살짝 위로 떠오르는 effect.
 *
 * 마감 차수(26-08-24) — `sub` 로 **두 번째 목적지**를 가진 카드를 만들 수 있다(사업
 * 대시보드 '기존고객사' 카드의 "해외 N곳"). 카드 하나에 링크가 둘이면 `<a>` 안에 `<a>` 를
 * 넣을 수 없으므로, sub 가 있는 카드만 겉이 `<div>` 가 되고 그 안에 링크 두 개가 선다.
 * sub 가 없는 카드의 DOM 은 종전 그대로다(`<a>` 하나) — 기존 시험·화면 회귀 없음.
 */
export function KpiCard({
  to,
  title,
  value,
  hint,
  variant = 'default',
  warningLevel,
  sub,
  icon,
}: {
  to: string;
  title: string;
  value: number | string;
  hint?: string;
  /** 'default' = blue tint, 'danger' = 빨간 톤 */
  variant?: 'default' | 'danger';
  /** value 가 0 보다 클 때 색상 강조 — amber (warning) / red (danger) */
  warningLevel?: 'amber' | 'red';
  /**
   * 보조 목적지(부제). 큰 숫자가 말하지 못하는 나머지를 **유실 없이** 덧붙이는 자리다 —
   * 값이 0 이면 부르는 쪽이 이 prop 자체를 넘기지 않는다(0 을 적으면 소음).
   */
  sub?: { to: string; label: string; title?: string };
  /**
   * 2026-10-27 오너 "나머지 것들도 색이랑 테마를 조정해" — 이름 앞 색 아이콘. 주면 카드는 흰 바탕에
   * 옅은 테두리가 되고(권역별 현황 카드와 같은 모양), 안 주면 종전처럼 팀 색으로 옅게 칠한다.
   */
  icon?: { name: IconName; color: string };
}) {
  // 2026-10-27 오너 — "버튼 크기 더 작게", "설명 문구도 다 빼", "손톱모양 그만". 왼쪽 색 띠와 큰
  // 둥근 모서리를 없애고, 칸 전체를 팀 색(AppShell 이 내려 주는 --section-accent)으로 옅게 칠한다.
  // 설명(hint)은 카드에 적지 않고 마우스를 올렸을 때만 보인다.
  const tint = variant === 'danger' ? 'var(--fg-danger)' : 'var(--section-accent, var(--brand))';
  const cardStyle: CSSProperties = {
    display: 'block',
    padding: '8px 12px',
    borderRadius: 6,
    border: icon
      ? '1px solid var(--border-card)'
      : `1px solid color-mix(in srgb, ${tint} 28%, var(--bg-surface))`,
    background: icon ? 'var(--bg-surface)' : `color-mix(in srgb, ${tint} 9%, var(--bg-surface))`,
    textDecoration: 'none',
    color: 'inherit',
    transition: 'box-shadow 0.1s ease-in-out',
    cursor: 'pointer',
  };

  const isWarning = warningLevel && typeof value === 'number' && value > 0;
  const valueColor = isWarning
    ? warningLevel === 'red'
      ? 'var(--fg-danger)'
      : 'var(--dday-warning)'
    : undefined;

  const body = (
    <>
      {icon ? (
        <IconLabel icon={icon.name} color={icon.color}>
          {title}
        </IconLabel>
      ) : (
        <div style={{ ...SHARED_CARD_TITLE, marginBottom: 2 }}>{title}</div>
      )}
      <div style={{ ...SHARED_BIG, fontSize: 20, lineHeight: 1.2, color: valueColor }}>{value}</div>
    </>
  );

  const lift = {
    onMouseEnter: (e: { currentTarget: HTMLElement }) => {
      e.currentTarget.style.boxShadow = 'var(--shadow-md)';
    },
    onMouseLeave: (e: { currentTarget: HTMLElement }) => {
      e.currentTarget.style.boxShadow = 'none';
    },
  };
  const tooltip = hint ?? `${title} → ${to}`;

  if (!sub) {
    return (
      <Link to={to} style={cardStyle} title={tooltip} {...lift}>
        {body}
      </Link>
    );
  }

  // 부제가 있는 카드 — 겉은 카드 상자(div), 안에 링크 둘. 큰 숫자 링크가 카드의 남는
  // 자리를 다 먹도록 block 으로 깔고(클릭 면적 유지) 부제를 그 아래 한 줄로 둔다.
  return (
    <div style={{ ...cardStyle, cursor: 'default' }} {...lift}>
      <Link
        to={to}
        style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
        title={tooltip}
      >
        {body}
      </Link>
      <Link to={sub.to} style={kpiSubLink} title={sub.title ?? `${sub.label} → ${sub.to}`}>
        {sub.label}
      </Link>
    </div>
  );
}

/**
 * 색 아이콘 + 이름 한 줄 — 숫자 카드의 이름 자리. 아이콘 바탕은 같은 색을 옅게 섞어 밝은 · 어두운
 * 화면 모두에서 읽힌다(패치노트 아이콘과 같은 모양). 아래 숫자와 6px 띄운다.
 */
export function IconLabel({
  icon,
  color,
  children,
}: {
  icon: IconName;
  color: string;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        // 2026-10-27 오너 "이런 글자들도 폰트 살짝씩 키우고" — 12 → 13.
        fontSize: 13,
        color: 'var(--fg-muted)',
        marginBottom: 6,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 22,
          borderRadius: 5,
          flex: '0 0 auto',
          color,
          background: `color-mix(in srgb, ${color} 14%, var(--bg-surface))`,
        }}
      >
        <Icon name={icon} size={14} aria-hidden />
      </span>
      {children}
    </div>
  );
}

const kpiSubLink: CSSProperties = {
  display: 'inline-block',
  marginTop: 2,
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--fg-brand)',
  textDecoration: 'none',
};

/**
 * Horizontal bar chart — funnel / distribution 둘 다 사용.
 * 각 row: label + bar (max 대비 width %) + 우측 count/value 표시.
 */
export function HorizontalBars({
  items,
  emptyText = '데이터가 없습니다.',
}: {
  items: Array<{
    key: string;
    label: string;
    count: number;
    value?: string; // optional 우측 표시 (KRW 등)
    color?: string;
    /** 막대 hover 설명 override — 기본은 `라벨: N건`. 단위가 '건'이 아닌 축에서 쓴다. */
    title?: string;
    /** 막대를 눌러 갈 곳이 있으면 지정. 주면 커서·키보드 접근까지 함께 붙는다. */
    onClick?: () => void;
  }>;
  emptyText?: string;
}) {
  if (items.length === 0) {
    return <div style={SHARED_EMPTY_HINT}>{emptyText}</div>;
  }
  const maxCount = Math.max(...items.map((i) => i.count), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((it) => {
        const widthPct = (it.count / maxCount) * 100;
        const color = it.color ?? 'var(--brand)';
        const rowTitle =
          it.title ??
          (it.value ? `${it.label}: ${it.count}건, ${it.value}` : `${it.label}: ${it.count}건`);
        return (
          <div
            key={it.key}
            // 갈 곳이 있는 막대만 버튼이 된다 — 없으면 지금까지처럼 그냥 그림이다.
            role={it.onClick ? 'button' : undefined}
            tabIndex={it.onClick ? 0 : undefined}
            onClick={it.onClick}
            onKeyDown={
              it.onClick
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      it.onClick?.();
                    }
                  }
                : undefined
            }
            // 설명(title)은 막대 자체가 이미 들고 있다 — 행에도 걸면 같은 글자가 두 겹이 된다.
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12,
              cursor: it.onClick ? 'pointer' : undefined,
            }}
          >
            <div
              style={{
                width: 100,
                flexShrink: 0,
                fontWeight: 500,
                fontSize: 12,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={it.label}
            >
              {it.label}
            </div>
            <div
              style={{
                flex: 1,
                background: 'var(--bg-sunken)',
                height: 22,
                borderRadius: 4,
                position: 'relative',
                overflow: 'hidden',
              }}
              title={rowTitle}
            >
              <div
                style={{
                  width: `${widthPct}%`,
                  height: '100%',
                  background: color,
                  borderRadius: 4,
                  transition: 'width 0.4s ease-out',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  left: 8,
                  top: 0,
                  lineHeight: '22px',
                  fontSize: 11,
                  color: widthPct > 50 ? 'var(--brand-on)' : 'var(--fg-default)',
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  pointerEvents: 'none',
                }}
              >
                {it.count}
              </span>
              {it.value && (
                <span
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: 0,
                    lineHeight: '22px',
                    fontSize: 11,
                    color: 'var(--fg-muted)',
                    fontFamily: 'var(--font-mono)',
                    pointerEvents: 'none',
                  }}
                >
                  {it.value}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// round10 B1/B3 — 경량 월별 세로 막대(차트 라이브러리 미사용). 제조 대시보드(생산/출고)
// + 사업 대시보드(계약건수/수익) 공용(중복 제거). "YYMM" → "M월" 라벨, 최대값 대비 px 높이.
// round10 B1 픽스: 막대 height 를 % 로 주면 부모 column(flex-end, 명시 height 없는 auto)에
// 대해 %가 auto 로 붕괴해 모든 막대가 3px 평선이 됐다 → 값 비례 px 로 직접 계산.
export function MonthlyTrendBars({
  points,
  color,
  emptyText,
  formatValue,
}: {
  points: Array<{ ym: string; count: number }>;
  color: string;
  emptyText: string;
  /** 막대 위 라벨 + tooltip 값 포맷. 기본 = 원시 숫자. (제조=대, 수익=만원/억 등 주입) */
  formatValue?: (n: number) => string;
}) {
  const total = points.reduce((s, p) => s + p.count, 0);
  if (points.length === 0 || total === 0) {
    return <div style={SHARED_EMPTY_HINT}>{emptyText}</div>;
  }
  const max = Math.max(...points.map((p) => p.count), 1);
  const fmt = formatValue ?? ((n: number) => String(n));
  const BAR_AREA_PX = 84;
  return (
    <div
      style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 120, paddingTop: 8 }}
      data-testid="monthly-trend-bars"
    >
      {points.map((p) => {
        const heightPx = p.count > 0 ? Math.max((p.count / max) * BAR_AREA_PX, 3) : 0;
        const monthNum = Number.parseInt(p.ym.slice(2), 10);
        return (
          <div
            key={p.ym}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              minWidth: 0,
            }}
            title={`${p.ym} · ${fmt(p.count)}`}
          >
            <span
              style={{
                fontSize: 10,
                color: 'var(--fg-muted)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {p.count > 0 ? fmt(p.count) : ''}
            </span>
            <div
              style={{
                width: '100%',
                height: `${heightPx}px`,
                background: color,
                borderRadius: '3px 3px 0 0',
                transition: 'height 0.4s ease-out',
              }}
            />
            <span style={{ fontSize: 9, color: 'var(--fg-subtle)' }}>
              {Number.isFinite(monthNum) ? `${monthNum}월` : p.ym}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Skeleton 의 한 box. variant 별 모양. */
export function SkeletonBox({ style }: { style?: CSSProperties }) {
  return <div style={{ ...SKELETON_BOX_BASE, ...style }} />;
}

/** KPI 카드 3-4 개 + chart 2-3 개 + list 의 skeleton. */
export function DashboardSkeleton({ kpiCount = 3 }: { kpiCount?: number }) {
  return (
    <>
      <ShimmerKeyframes />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* KPI row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${kpiCount}, 1fr)`,
            gap: 16,
          }}
        >
          {Array.from({ length: kpiCount }, (_, i) => (
            <div key={i} style={SHARED_CARD}>
              <SkeletonBox style={{ width: '60%', height: 12, marginBottom: 12 }} />
              <SkeletonBox style={{ width: '40%', height: 32 }} />
              <SkeletonBox style={{ width: '70%', height: 11, marginTop: 8 }} />
            </div>
          ))}
        </div>
        {/* Chart card */}
        <div style={SHARED_CARD}>
          <SkeletonBox style={{ width: 200, height: 12, marginBottom: 16 }} />
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
              }}
            >
              <SkeletonBox style={{ width: 80, height: 14 }} />
              <SkeletonBox style={{ flex: 1, height: 20 }} />
            </div>
          ))}
        </div>
        {/* Chip / table card */}
        <div style={SHARED_CARD}>
          <SkeletonBox style={{ width: 180, height: 12, marginBottom: 12 }} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[0, 1, 2, 3].map((i) => (
              <SkeletonBox key={i} style={{ width: 100, height: 24, borderRadius: 999 }} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

/** Dashboard error state. */
export function DashboardError({ onRetry }: { onRetry?: () => void }) {
  return (
    <div
      style={{
        padding: 24,
        border: '1px solid var(--fg-danger)',
        borderRadius: 'var(--r-card)',
        background: 'var(--bg-danger-subtle)',
        color: 'var(--fg-danger)',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <span>대시보드를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          style={{
            padding: '4px 14px',
            borderRadius: 'var(--r-btn)',
            border: '1px solid var(--fg-danger)',
            background: 'transparent',
            color: 'var(--fg-danger)',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          다시 시도
        </button>
      )}
    </div>
  );
}

/** Auto-refresh footer hint. */
export function AutoRefreshHint({ isFetching }: { isFetching: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 4,
        fontSize: 11,
        color: 'var(--fg-muted)',
        marginTop: -8,
      }}
    >
      <Icon name="refresh" size={12} color="var(--fg-muted)" />
      <span>5분마다 자동으로 새로고침합니다.{isFetching ? ' 새로 불러오는 중…' : ''}</span>
    </div>
  );
}

/** Section wrapper. */
export function Section({
  title,
  children,
  extra,
  titleExtra,
  style,
}: {
  title: string;
  children: ReactNode;
  extra?: ReactNode;
  /** 제목 바로 옆(왼쪽)에 붙는 조작 — 권역별 현황의 달 선택 등. extra 는 오른쪽 끝. */
  titleExtra?: ReactNode;
  /** 칸 상자에 덧붙일 모양 — 옆 칸과 높이를 맞출 때(flex: 1) 등. */
  style?: CSSProperties;
}) {
  return (
    <div style={{ ...SHARED_CARD, ...style }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          // 2026-10-27 오너 "간격 띄우고" — 제목 줄 오른쪽 단추가 바로 아래 표 머리줄에 붙었다.
          marginBottom: extra ? 8 : 0,
        }}
      >
        {titleExtra ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ ...SECTION_TITLE, marginBottom: 0 }}>{title}</div>
            {titleExtra}
          </div>
        ) : (
          <div style={SECTION_TITLE}>{title}</div>
        )}
        {extra}
      </div>
      {children}
    </div>
  );
}
